import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import type { ChatSendPayload } from "../components/Chat/chatTypes";
import { markBranchAwaitingAiName } from "../lib/aiBranchNames";
import { clientSendContext } from "../lib/clientSendContext";
import { useNavigateOnBranchChange } from "./useNavigateOnBranchChange";

export function useWorkflowChat(
  conversationId: string,
  branchId: string,
  isOnMainBranch: boolean
) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  const [identity, setIdentity] = useState({
    conversationId,
    key: 0,
    handoff: null as string | null,
  });
  if (identity.conversationId !== conversationId) {
    setIdentity({
      conversationId,
      key: identity.key + (identity.handoff === conversationId ? 0 : 1),
      handoff: null,
    });
  }

  function onSend(payload: ChatSendPayload, onError: () => void) {
    let forkBranchId: string | undefined;
    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      setIdentity((current) =>
        current.handoff === forkBranchId
          ? { ...current, handoff: null }
          : current
      );
      onError();
    };
    return navigateOnBranchChange((branchParams) => {
      if (isOnMainBranch) {
        forkBranchId = branchParams.forkBranchId;
        markBranchAwaitingAiName(forkBranchId);
        setIdentity((current) => ({
          ...current,
          handoff: branchParams.forkBranchId,
        }));
      }
      const result = z.mutate(
        sharedMutators.sendMessage({
          ...branchParams,
          entryId: nanoid(12),
          text: payload.text,
          isDictated: payload.isDictated,
          attachments: payload.attachments,
          selectedStep: payload.selectedStep,
          interruptEntryId: payload.interruptEntryId,
          planMode: payload.planMode,
          effort: payload.effort,
          chatModel: payload.chatModel,
          actionKind: payload.actionKind,
          ...clientSendContext(),
        })
      );
      void result.server.then((r) => {
        if (r.type === "error") restore();
      }, restore);
      return result;
    }).then((nextBranchId) => {
      if (isOnMainBranch && !nextBranchId) restore();
    }, restore);
  }

  return { chatKey: identity.key, onSend };
}
